import { useToken } from "@gluestack-style/react";
import CartOrderSummary from "components/cart/CartOrderSummary";
import { CartPickupLocationModal } from "components/cart/CartPickupLocationModal";
import { useUser } from "components/clerk";
import CheckoutProgress from "components/general/CheckoutProgress";
import Container from "components/general/Container";
import ScrollContainer from "components/general/ScrollContainer";
import { SiteSelectButton } from "components/general/SiteSelectButton";
import { Box, Button, HStack } from "components/ui";
import { ParagraphSmall } from "components/ui/Typography";
import { useLocalSearchParams, useNavigation, useSegments } from "expo-router";
import { TriangleAlertIcon } from "lucide-react-native";
import React, { useEffect, useRef, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useGetCartByIdQuery, useGetCartCurrentQuery, useGetCompanyLocationsQuery, usePutCartByIdMutation } from "store/abcoApi";
import { Value } from "types/select.types";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { SafeAreaView, Text, View } from "../components/ui";

export function canBeFulfilled(line: any, cart: any) {
	const availability = line.item.itemAvailability?.find((x: any) => x.locationId === cart?.locationId);

	const availabilityStatus = availability?.status ?? "";
	const classification = availability?.classification ?? "";
	const quantity = availability?.quantity ?? 0;
	const requiredQty = line.quantity ?? 1;
	const substituteCount = line.item.substituteCollection?.length ?? 0;
	const isInsufficient = quantity < requiredQty;
	const isAvailableElsewhere = line.item.itemAvailability?.some((x: any) => x.locationId !== cart?.locationId && x.quantity >= requiredQty && x.status !== "OutOfStock") ?? false;

	// For discontinued items, sum all available quantities across all locations
	const totalAvailableQuantity =
		classification === "Discontinued"
			? line.item.itemAvailability?.reduce((sum: number, location: any) => {
					return sum + (location.status !== "OutOfStock" ? location.quantity || 0 : 0);
				}, 0) || 0
			: 0;


	switch( classification )
	{
		case "Discontinued":
			// No stock anywhere and no substitutes
			if (totalAvailableQuantity === 0 && substituteCount === 0) return 60;

			// Handle "elsewhere" availability first
			if (isAvailableElsewhere) {
				return isInsufficient ? 41 : 40; // Status 41 or 40
			}
			
			// Out of stock but has substitutes and not available elsewhere
			if (availabilityStatus === "OutOfStock" && substituteCount > 0) return 51;
			
			// Any insufficient quantity scenario (global or local)
			if (totalAvailableQuantity < requiredQty || isInsufficient) return 50;
			
			return 60; 

		case "Special Order Only":
			if ( availabilityStatus !== "InStock" && isInsufficient && substituteCount > 0 ) {
				return 40;
			}
			break;

		default:
			if ( availabilityStatus !== "InStock" && isInsufficient && substituteCount > 0 ) {
				return 40;
			}
			break;
	}

	return 0;
}

export function sortLines(lines: any, cart: any) {
	return [...lines].sort((a, b) => {
		const priorityA = canBeFulfilled(a, cart);
		const priorityB = canBeFulfilled(b, cart);

		if (priorityA !== priorityB) {
			return priorityB - priorityA; // higher number = higher priority = should be at top
		}

		// fallback to stockcode sort
		const stockCodeA = a.item.stockcode || "";
		const stockCodeB = b.item.stockcode || "";
		if (stockCodeA > stockCodeB) return -1;
		if (stockCodeA < stockCodeB) return 1;
		return 0;
	});
}

export function CartCheckoutLayout({ children, canEdit, noReplaceItems }: { children: React.ReactNode; canEdit: boolean; noReplaceItems?: Set<string> }) {
	const { id } = useLocalSearchParams<{ id: string }>();
	const { data: cart, isSuccess } = id ? useGetCartByIdQuery({ id: parseInt(id) }) : useGetCartCurrentQuery();
	const [isApprover, setIsApprover] = useState(false);
	const { height } = useWindowDimensions();
	const [updateCart] = usePutCartByIdMutation();
	const navigation = useNavigation();
	const isMobile = useIsMobile();
	const [cartLines, setCartLines]: any = useState([]);
	const bottomSheetRef = useRef(null);
	const [snapIndex, setSnapIndex] = useState(0);
	const handleSnap = (index: number) => {
		setSnapIndex(index);
	};
	const segments = useSegments();
	const currentStep = parseFloat(segments[segments.length - 1].replace("(step", "").replace(")", ""));
	let { user, isSignedIn } = useUser();
	const resolvedWarningMain = useToken("colors", "warningmain");
	const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
	const scrollViewRef = useRef(null);
	const { data: locations, isLoading: isLoadingLocations } = useGetCompanyLocationsQuery();
	const [selectedLocation, setSelectedLocation] = useState<any>(null);
	const [showOrderProgress, setShowOrderProgress] = useState(false);

	let buttonText = "Checkout";
	let buttonLink = "cart/(step2)/index";

	switch (currentStep) {
		case 2:
			buttonText = "Continue to payment";
			buttonLink = "cart/(step3)/index";
			break;
		case 3:
			buttonText = "Submit Order";
			buttonLink = "cart/(step4)/index";
			break;
	}
	useEffect(() => {
		let currentUserId = user && user.externalId ? parseInt(user.externalId ?? "") : "";
		let isAwaitingApproval = cart?.approvalHistory && cart?.approvalHistory.length > 0 && cart.hasCompleted === false ? true : false;
		if (currentUserId) {
			setIsApprover(cart?.potentialApprover?.some((approver: any) => approver.id === currentUserId) && cart.userId != currentUserId ? true : false);
		}

		if (isSuccess && cart) {
			setCartLines(sortLines(cart?.lines, cart));
		}

		if (isAwaitingApproval) {
			setShowOrderProgress(true);
		}
	}, [cart, isSuccess]);
	const updateSite = (site: Value) => {
		updateCart({
			cart: {
				// @ts-ignore
				siteId: site.siteId == null ? 0 : site.siteId,
			},
			id: cart?.id as number,
		});
	};

	if (isMobile) {
		return (
			<SafeAreaView style={{ height: "100%" }}>
				<View height={"$full"} backgroundColor="$basicwhite">
					{children}
					<CartOrderSummary noReplaceItems={noReplaceItems} />
				</View>
			</SafeAreaView>
		);
	}

	return (
		<>
			<ScrollContainer>
				<Container>
					<CheckoutProgress isApprover={isApprover} value={showOrderProgress ? 1 : null} isLinkDisabled={showOrderProgress ? true : false} />

					<Box position="relative" flexDirection="row" gap={32}>
						<Box flex={1}>
							{!cart?.siteId && !cart?.pickupLocationId && isSignedIn && currentStep === 1 && (
								<Box bg="$neutral25" gap={16} padding={24} borderRadius={6}>
									<Box flexDirection="row" gap={12} borderRadius={6} borderWidth={1} borderColor="$warningbright" bg="$warningdisabled" paddingVertical={6} paddingHorizontal={8}>
										<TriangleAlertIcon size={18} color={resolvedWarningMain} />
										{!cart?.siteId && !cart?.pickupLocationId && (
											<Box flex={1}>
												<ParagraphSmall>
													<Text style={{ fontFamily: "Roboto_700Bold" }}>Please select a site to continue. </Text>
													<Text>We noticed that you haven't chosen a site yet. Selecting the appropriate site helps us provide accurate information.</Text>
												</ParagraphSmall>
											</Box>
										)}
									</Box>
									<HStack alignItems="center" gap={16}>
										<Box flex={isMobile ? 1 : undefined}>
											<SiteSelectButton
												value={{
													id: cart?.siteId || 0,
													name: cart?.siteName || "",
												}}
												title="Site"
												onSelect={updateSite}
											>
												<Button variant="primary-light" borderRadius={12} gap={4} justifyContent="flex-start">
													<ParagraphSmall
														sx={{
															color: "$basicwhite",
														}}
													>
														Select Site for Delivery
													</ParagraphSmall>
												</Button>
											</SiteSelectButton>
										</Box>
										<Box flex={isMobile ? 1 : undefined}>
											<Button borderRadius={12} gap={4} variant="outline" onPress={() => setIsPickupModalOpen(true)}>
												<ParagraphSmall
													sx={{
														color: "$primarylight",
													}}
												>
													Pickup order
												</ParagraphSmall>
											</Button>
										</Box>

										{!isMobile && (
											<CartPickupLocationModal
												isVisible={isPickupModalOpen}
												onClose={() => setIsPickupModalOpen(false)}
												onLocationSelect={(location) => {
													setSelectedLocation(location);
													updateCart({
														cart: {
															pickupLocationId: location.id,
															shippingType: "pickup",
														},
														id: cart?.id as number,
													});
													setIsPickupModalOpen(false);
												}}
												cartId={cart?.id as number}
												selectedLocationId={cart?.pickupLocationId || undefined}
											/>
										)}
									</HStack>
								</Box>
							)}

							{children}
						</Box>

						{!isMobile && <CartOrderSummary noReplaceItems={noReplaceItems} />}
					</Box>
				</Container>
			</ScrollContainer>
		</>
	);
}
